import { getItem } from './items.js';
export const WALL_GLYPHS = new Set(['#', '█', '▒', '┌', '┐', '└', '┘', '─', '│', '┴', '┬', '├', '┤', '┼']);
export function isWallGlyph(char) {
    return WALL_GLYPHS.has(char);
}
import { idx, isWall, tileAt } from './maze.js';
import { generateBiomeMaze } from './procgen.js';
import { BIOMES } from './biomes.js';
import { randomInt } from './random.js';
import { getEffectSum, getEffectMult } from './effects.js';
import { createTutorialQuest, handleQuestProgress, generateContract } from './quests.js';
const DIRS = {
    up: { x: 0, y: -1 }, down: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 }, none: { x: 0, y: 0 }
};
export function createGame(options = 1) {
    const seed = typeof options === 'number' ? options : (options.seed ?? 1);
    const invIds = typeof options === 'object' && options.inventory ? options.inventory : ['blink', 'dash'];
    const startInv = invIds.map(id => {
        const it = getItem(id);
        return it ? { ...it, cooldownTicks: 0 } : undefined;
    }).filter(Boolean);
    const isBossLevel = seed % 5 === 0; // Every 5th level is a boss
    const modifiers = [];
    if (seed % 3 === 0)
        modifiers.push('icy_floors');
    if (seed % 7 === 0)
        modifiers.push('hyper_enemies');
    if (seed % 11 === 0)
        modifiers.push('power_drain');
    const biomeIds = ['tutorial', 'neon_alley', 'toxic_sewers', 'core_reactor'];
    const biomeId = biomeIds[seed % biomeIds.length];
    const biome = BIOMES[biomeId];
    const maze = generateBiomeMaze(seed, biomeId);
    // Use biome's enemyMix to populate enemies
    const enemies = [];
    // Rebalance: Daily seed difficulty scaling
    // Seed is often YYYYMMDD. We use modulo 5 to add 0-2 extra enemies, 
    // and biome base sizes determine the rest.
    const extraEnemies = seed % 5 < 3 ? seed % 5 : 0;
    let eCount = Math.floor(maze.width * maze.height / 120) + extraEnemies;
    for (let i = 0; i < eCount; i++) {
        const kind = biome.enemyMix[i % biome.enemyMix.length];
        // Pick random empty tile for enemy (simplified, skipping safe distance check for now)
        let pos = { x: 2, y: 2 };
        for (let attempt = 0; attempt < 20; attempt++) {
            let x = randomInt(seed, i * 10 + attempt, 0, 0, maze.width - 1);
            let y = randomInt(seed, i * 10 + attempt + 1, 0, 0, maze.height - 1);
            if (maze.tiles[y * maze.width + x] === '.' || maze.tiles[y * maze.width + x] === ' ') {
                pos = { x, y };
                break;
            }
        }
        enemies.push({ id: `${kind}-${i}`, kind: kind, pos, dir: 'up' });
    }
    return {
        seed,
        tick: 0,
        biomeId,
        width: maze.width,
        height: maze.height,
        tiles: [...maze.tiles],
        player: { id: 'player', kind: 'player', pos: maze.start, dir: 'none' },
        enemies: [
            ...enemies,
            ...(isBossLevel ? [{ id: 'boss-1', kind: 'boss', pos: { x: 6, y: 6 }, dir: 'down', hp: 3, maxHp: 3, bossPhase: 1 }] : [])
        ],
        sectorModifiers: modifiers,
        score: 0,
        lives: 3,
        powerTicks: 0, shieldTicks: 0, invincibleTicks: 0,
        inventory: startInv,
        quests: [createTutorialQuest(0)],
        factionReputation: { maw_corps: 0, neon_syndicate: 0, scrappers: 0 },
        runStatus: 'running',
        log: []
    };
}
function moveIfOpen(state, pos, dir) {
    const d = DIRS[dir];
    const nx = pos.x + d.x;
    const ny = pos.y + d.y;
    if (isWall(tileAt(state.tiles, state.width, nx, ny)))
        return pos;
    return { x: nx, y: ny };
}
function getTargetPos(state, e) {
    const p = state.player;
    if (e.kind === 'hunter') {
        return p.pos;
    }
    if (e.kind === 'ambusher') {
        const d = DIRS[p.dir];
        return { x: p.pos.x + d.x * 4, y: p.pos.y + d.y * 4 };
    }
    return p.pos; // fallback for others
}
function chooseEnemyDir(state, enemy, enemyIndex) {
    if ((enemy.stunTicks ?? 0) > 0)
        return 'none';
    const isPowered = state.powerTicks > 0;
    const isFrightened = isPowered || (enemy.frightenedTicks ?? 0) > 0;
    const dirs = ['up', 'down', 'left', 'right'];
    let best = 'none';
    let bestScore = isFrightened ? -1 : Number.POSITIVE_INFINITY;
    const rOffset = randomInt(state.seed, state.tick, enemyIndex, 0, 4);
    // patroller moves randomly when not frightened
    if (!isFrightened && enemy.kind === 'patroller') {
        const validDirs = dirs.filter(d => {
            const p = moveIfOpen(state, enemy.pos, d);
            const isReverse = (d === 'up' && enemy.dir === 'down') || (d === 'down' && enemy.dir === 'up') || (d === 'left' && enemy.dir === 'right') || (d === 'right' && enemy.dir === 'left');
            return (p.x !== enemy.pos.x || p.y !== enemy.pos.y) && !isReverse;
        });
        if (validDirs.length > 0)
            return validDirs[rOffset % validDirs.length];
        const allValid = dirs.filter(d => { const p = moveIfOpen(state, enemy.pos, d); return p.x !== enemy.pos.x || p.y !== enemy.pos.y; });
        if (allValid.length > 0)
            return allValid[rOffset % allValid.length];
        return 'none';
    }
    const target = getTargetPos(state, enemy);
    for (let i = 0; i < 4; i++) {
        const d = dirs[(i + rOffset) % 4];
        const p = moveIfOpen(state, enemy.pos, d);
        if (p.x === enemy.pos.x && p.y === enemy.pos.y)
            continue;
        // Prevent reverse direction unless trapped
        const isReverse = (d === 'up' && enemy.dir === 'down') || (d === 'down' && enemy.dir === 'up') || (d === 'left' && enemy.dir === 'right') || (d === 'right' && enemy.dir === 'left');
        if (isReverse) {
            const otherValid = dirs.filter(od => { const op = moveIfOpen(state, enemy.pos, od); return (op.x !== enemy.pos.x || op.y !== enemy.pos.y) && od !== d; });
            if (otherValid.length > 0)
                continue;
        }
        const dist = Math.abs(p.x - target.x) + Math.abs(p.y - target.y);
        if (isFrightened) {
            if (dist > bestScore) {
                bestScore = dist;
                best = d;
            }
        }
        else {
            if (dist < bestScore) {
                bestScore = dist;
                best = d;
            }
        }
    }
    return best;
}
export function stepGame(state, action) {
    if (state.runStatus !== 'running')
        return state;
    const next = structuredClone(state);
    handleQuestProgress(next.quests, 'survive_ticks', 1);
    next.tick++;
    const scoreMult = getEffectMult(next, 'score_multiplier');
    const cdReduc = getEffectSum(next, 'cooldown_reduction');
    const powerDurMult = 1 + getEffectSum(next, 'power_duration') / 10;
    const actualPowerDurMult = next.sectorModifiers.includes('power_drain') ? powerDurMult * 0.5 : powerDurMult;
    const baseSpeed = 1 + getEffectSum(next, 'speed');
    let desired = action.dir ?? next.player.dir;
    if (next.sectorModifiers.includes('icy_floors') && action.dir && action.dir !== next.player.dir && action.dir !== 'none') {
        if (randomInt(next.seed, next.tick, 888, 0, 100) < 20) {
            desired = next.player.dir;
            next.log.push('slip_on_ice');
        }
    }
    next.player.dir = desired;
    let stepsToTake = Math.floor(baseSpeed);
    // Rebalance: Support fractional speed via probability
    if (randomInt(next.seed, next.tick, 999, 0, 100) < (baseSpeed % 1) * 100) {
        stepsToTake++;
    }
    for (let s = 0; s < stepsToTake; s++) {
        next.player.pos = moveIfOpen(next, next.player.pos, desired);
    }
    const ti = idx(next.player.pos.x, next.player.pos.y, next.width);
    const tile = next.tiles[ti];
    if (tile === '.' || tile === 'o') {
        next.tiles[ti] = ' ';
        next.log.push(tile === 'o' ? 'eat_power' : 'eat_dot');
        const basePts = tile === 'o' ? 50 : 10;
        next.score += Math.floor(basePts * scoreMult);
        if (tile === 'o') {
            next.powerTicks = Math.floor(35 * actualPowerDurMult);
            next.enemies.forEach(e => {
                if (e.dir === 'up')
                    e.dir = 'down';
                else if (e.dir === 'down')
                    e.dir = 'up';
                else if (e.dir === 'left')
                    e.dir = 'right';
                else if (e.dir === 'right')
                    e.dir = 'left';
            });
        }
        handleQuestProgress(next.quests, 'collect_shards', 1);
    }
    // Process completed quests
    for (let i = next.quests.length - 1; i >= 0; i--) {
        const q = next.quests[i];
        if (q.complete) {
            if (q.reward.xp) {
                next.log.push(`XP_EARNED:${q.reward.xp}`);
            }
            if (q.reward.factionRep) {
                for (const [fac, amt] of Object.entries(q.reward.factionRep)) {
                    next.factionReputation[fac] += amt;
                }
            }
            if (q.id.startsWith('tutorial_')) {
                const idx = parseInt(q.id.split('_')[1], 10);
                const TUTORIAL_CHAIN_LENGTH = 3; // hardcoded logic for sim.ts
                if (idx < TUTORIAL_CHAIN_LENGTH) {
                    next.quests.push(createTutorialQuest(idx));
                }
                else {
                    next.quests.push(generateContract(next.seed + next.tick, 1));
                }
            }
            else {
                next.quests.push(generateContract(next.seed + next.tick, 1));
            }
            next.quests.splice(i, 1);
        }
    }
    if (action.useItemId) {
        const item = next.inventory.find(i => i.id === action.useItemId);
        if (item && (!item.cooldownTicks || item.cooldownTicks <= 0)) {
            if (item.id === 'blink') {
                next.player.pos = moveIfOpen(next, moveIfOpen(next, next.player.pos, desired), desired);
                next.log.push('blink');
            }
            else if (item.id === 'dash') {
                next.player.pos = moveIfOpen(next, next.player.pos, desired);
                next.log.push('dash');
            }
            else if (item.id === 'shield_gen') {
                next.shieldTicks = 30;
                next.log.push('shield_activated');
            }
            else if (item.id === 'ghost_cloak') {
                next.invincibleTicks = 40;
                next.log.push('ghost_cloak_activated');
            }
            else if (item.id === 'stun_grenade') {
                next.enemies.forEach(e => { e.stunTicks = 15; });
                next.log.push('stun_grenade_used');
            }
            else if (item.id === 'heal_potion') {
                next.lives++;
                next.log.push('healed');
            }
            else if (item.id === 'smart_bomb') {
                next.enemies.forEach(e => { e.pos = { x: 7, y: 7 }; });
                next.log.push('smart_bomb_used');
            }
            else if (item.id === 'time_warp') {
                next.enemies.forEach(e => { e.stunTicks = (e.stunTicks || 0) + 20; });
                next.powerTicks += 20;
                next.log.push('time_warp_used');
            }
            if (item.maxCooldownTicks) {
                const reduction = Math.floor(item.maxCooldownTicks * Math.min(cdReduc, 0.8));
                item.cooldownTicks = item.maxCooldownTicks - reduction;
            }
            if (item.charges !== undefined) {
                item.charges--;
                if (item.charges <= 0) {
                    next.inventory = next.inventory.filter(i => i.id !== item.id);
                }
            }
        }
    }
    next.inventory.forEach(i => {
        if (i.cooldownTicks && i.cooldownTicks > 0)
            i.cooldownTicks--;
    });
    if (next.powerTicks > 0)
        next.powerTicks--;
    if (next.shieldTicks > 0)
        next.shieldTicks--;
    if (next.invincibleTicks > 0)
        next.invincibleTicks--;
    const isHyper = next.sectorModifiers.includes('hyper_enemies') && next.tick % 3 === 0;
    // Boss special actions
    const newEnemies = [];
    next.enemies.forEach(e => {
        if (e.kind === 'boss' && e.bossPhase && e.bossPhase >= 2 && next.tick % 50 === 0) {
            newEnemies.push({
                id: `snare-boss-${next.tick}`,
                kind: 'snare',
                pos: { x: e.pos.x, y: e.pos.y },
                dir: 'none'
            });
            next.log.push(`boss_spawned_snare:${e.id}`);
        }
    });
    next.enemies.push(...newEnemies);
    next.enemies.forEach((e, i) => {
        if ((e.stunTicks ?? 0) > 0) {
            e.stunTicks--;
        }
        else {
            const isHyper = next.sectorModifiers.includes('hyper_enemies');
            const isFastModifier = e.modifiers?.includes('fast');
            let steps = 1;
            if (e.kind !== 'boss') {
                if (isHyper && randomInt(next.seed, next.tick, i * 13, 0, 100) < 30)
                    steps++;
                if (isFastModifier && randomInt(next.seed, next.tick, i * 17, 0, 100) < 30)
                    steps++;
            }
            for (let s = 0; s < steps; s++) {
                e.dir = chooseEnemyDir(next, e, i);
                e.pos = moveIfOpen(next, e.pos, e.dir);
            }
        }
    });
    for (const e of next.enemies) {
        if (e.pos.x === next.player.pos.x && e.pos.y === next.player.pos.y) {
            if (next.powerTicks > 0) {
                if (e.kind === 'boss') {
                    e.hp = (e.hp ?? 3) - 1;
                    next.score += Math.floor(500 * scoreMult);
                    next.log.push(`boss_hit:${e.id}`);
                    if (e.hp <= 0) {
                        e.pos = { x: 7, y: 7 };
                        next.log.push(`boss_defeated:${e.id}`);
                        handleQuestProgress(next.quests, 'kill_enemies', 1);
                        e.hp = 0; // mark for removal
                    }
                    else {
                        e.pos = { x: 7, y: 7 };
                        e.bossPhase = (e.bossPhase ?? 1) + 1;
                    }
                }
                else {
                    next.score += Math.floor(200 * scoreMult);
                    e.pos = { x: 7, y: 7 };
                    next.log.push(`banished:${e.id}`);
                    handleQuestProgress(next.quests, 'kill_enemies', 1);
                }
            }
            else if (next.invincibleTicks > 0) {
                // do nothing, player is invincible
            }
            else if (next.shieldTicks > 0) {
                next.shieldTicks = 0; // shield breaks
                next.invincibleTicks = 10; // brief i-frames after break
                next.log.push(`shield_broke_from:${e.id}`);
                e.pos = { x: 7, y: 7 };
            }
            else {
                next.lives--;
                next.log.push(`hit:${e.id}`);
                next.player.pos = { x: 1, y: 1 };
                if (next.lives <= 0)
                    next.runStatus = 'lost';
            }
        }
    }
    next.enemies = next.enemies.filter(e => !(e.kind === 'boss' && e.hp !== undefined && e.hp <= 0));
    const remaining = next.tiles.filter(t => t === '.' || t === 'o').length;
    if (remaining === 0 || tileAt(next.tiles, next.width, next.player.pos.x, next.player.pos.y) === 'E' && next.score >= 150)
        next.runStatus = 'won';
    return next;
}
export function greedyBotAction(state) {
    // BFS pathfinding & risk scoring
    const width = state.width;
    const height = state.height;
    const tiles = state.tiles;
    const enemies = state.enemies;
    const px = state.player.pos.x;
    const py = state.player.pos.y;
    const dirs = ['up', 'down', 'left', 'right'];
    const dVecs = [{ x: 0, y: -1 }, { x: 0, y: 1 }, { x: -1, y: 0 }, { x: 1, y: 0 }];
    const isWallTile = (x, y) => {
        const t = tileAt(tiles, width, x, y);
        return isWallGlyph(t);
    };
    // Compute enemy danger map
    const danger = new Int32Array(width * height).fill(999);
    const q = [];
    if (state.powerTicks === 0 && state.invincibleTicks === 0) {
        for (const e of enemies) {
            if ((e.stunTicks ?? 0) <= 0) {
                danger[e.pos.y * width + e.pos.x] = 0;
                q.push({ x: e.pos.x, y: e.pos.y, d: 0 });
            }
        }
    }
    let head = 0;
    while (head < q.length) {
        const curr = q[head++];
        if (curr.d > 5)
            continue;
        for (let i = 0; i < 4; i++) {
            const nx = curr.x + dVecs[i].x;
            const ny = curr.y + dVecs[i].y;
            if (!isWallTile(nx, ny)) {
                const nd = curr.d + 1;
                if (nd < danger[ny * width + nx]) {
                    danger[ny * width + nx] = nd;
                    q.push({ x: nx, y: ny, d: nd });
                }
            }
        }
    }
    // BFS from player to find targets
    let bestMove = 'none';
    let bestScore = -999999;
    // To avoid purely deterministic getting stuck, add slight noise to scores
    const rOffset = randomInt(state.seed, state.tick, 101, 0, 100);
    // Consider immediate neighbors
    for (let i = 0; i < 4; i++) {
        const d = dirs[i];
        const nx = px + dVecs[i].x;
        const ny = py + dVecs[i].y;
        if (isWallTile(nx, ny))
            continue;
        if (nx === px && ny === py)
            continue;
        // Evaluate this move
        let score = 0;
        const t = tileAt(tiles, width, nx, ny);
        if (t === '.')
            score += 10;
        if (t === 'o')
            score += 50;
        if (t === 'E' && state.score >= 150)
            score += 1000;
        // Penalty for danger
        const dang = danger[ny * width + nx];
        if (dang <= 2)
            score -= (500 - dang * 100); // strong penalty for being near enemies
        if (dang === 0)
            score -= 2000; // fatal
        // Bonus for chasing enemies if powered
        if (state.powerTicks > 0) {
            let nearestEnDist = 999;
            for (const e of enemies) {
                const distToEn = Math.abs(e.pos.x - nx) + Math.abs(e.pos.y - ny);
                if (distToEn < nearestEnDist)
                    nearestEnDist = distToEn;
            }
            if (nearestEnDist < 5)
                score += (20 - nearestEnDist * 4);
        }
        // Let's add BFS logic for finding nearest dots / powers
        let nearestGoodDist = 999;
        // Quick BFS from this neighbor
        const ndist = new Int32Array(width * height).fill(999);
        ndist[ny * width + nx] = 0;
        const nq = [{ x: nx, y: ny, d: 0 }];
        let nhead = 0;
        let foundGood = false;
        while (nhead < nq.length && nhead < 1500) {
            const curr = nq[nhead++];
            const ct = tileAt(tiles, width, curr.x, curr.y);
            // Stop if found a good target
            if (ct === '.' || ct === 'o' || (ct === 'E' && state.score >= 150)) {
                nearestGoodDist = curr.d;
                foundGood = true;
                if (ct === 'o')
                    nearestGoodDist -= 5;
                if (ct === 'E' && state.score >= 150)
                    nearestGoodDist -= 20;
                break;
            }
            for (let j = 0; j < 4; j++) {
                const nnx = curr.x + dVecs[j].x;
                const nny = curr.y + dVecs[j].y;
                if (!isWallTile(nnx, nny) && danger[nny * width + nnx] > 1) {
                    const nd = curr.d + 1;
                    if (nd < ndist[nny * width + nnx]) {
                        ndist[nny * width + nnx] = nd;
                        nq.push({ x: nnx, y: nny, d: nd });
                    }
                }
            }
        }
        if (foundGood) {
            score += (100 - nearestGoodDist * 2);
        }
        score += (rOffset % 3); // small random tie-breaker
        if (score > bestScore) {
            bestScore = score;
            bestMove = d;
        }
    }
    // Inventory usage logic
    let useItemId = undefined;
    const currentDanger = danger[py * width + px];
    if (currentDanger <= 2 && state.powerTicks === 0 && state.shieldTicks === 0 && state.invincibleTicks === 0) {
        const shield = state.inventory.find(i => i.id === 'shield_gen' && (!i.cooldownTicks || i.cooldownTicks <= 0));
        if (shield)
            useItemId = 'shield_gen';
        else {
            const stun = state.inventory.find(i => i.id === 'stun_grenade' && (!i.cooldownTicks || i.cooldownTicks <= 0));
            if (stun)
                useItemId = 'stun_grenade';
            else {
                const ghost = state.inventory.find(i => i.id === 'ghost_cloak' && (!i.cooldownTicks || i.cooldownTicks <= 0));
                if (ghost)
                    useItemId = 'ghost_cloak';
                else {
                    const bomb = state.inventory.find(i => i.id === 'smart_bomb' && (!i.cooldownTicks || i.cooldownTicks <= 0));
                    if (bomb)
                        useItemId = 'smart_bomb';
                    else {
                        const dash = state.inventory.find(i => i.id === 'dash' && (!i.cooldownTicks || i.cooldownTicks <= 0));
                        if (dash)
                            useItemId = 'dash';
                    }
                }
            }
        }
    }
    return { dir: bestMove, useItemId };
}
export function runEpisode(seed = 1, maxTicks = 250) {
    let s = createGame({ seed });
    for (let i = 0; i < maxTicks && s.runStatus === 'running'; i++)
        s = stepGame(s, greedyBotAction(s));
    return s;
}
